import React, { useEffect } from "react";
import { LocalStorageService } from "../service/LocalStorageService";
import { SessionService } from "../service/SessionService";
import ApiService from "../service/ApiService";
import { clearApiCache } from "../service/ApiCache";
import Helper from "../Helper";

export const PERMISSIONS = {
    APPOINTMENT_CREATE_EDIT: 1,
    ENTITLEMENT_VIEW: 2,
    APPOINTMENT_DELETE: 3,
    APPOINTMENT_VIEW_DELETE: 4,
    APPOINTMENT_PRINTER_BARCODE: 5,
    APPOINTMENT_SEND_BARCODE: 6,
    FIRM_USER_MANAGER: 8,
    FIRM_DOCTOR_MANAGER: 10,
    FIRM_INSTITUTION_MANAGER: 9,
    FIRM_OPERATION_MANAGER: 11,
    FIRM_UNIT_MANAGER: 12,
    APPOINTMENT_VIEW: 13,
    CASH_REGISTER_VIEW: 14,
    EXPENSE_CREATE_EDIT: 15,
    EXPENSE_DELETE: 16,
    EXPENSE_VIEW: 17,
    EXPENSE_VIEW_DELETE: 18,
    PAYMENT_CREATE_EDIT: 19,
    PAYMENT_DELETE: 20,
    PAYMENT_VIEW: 21,
    PAYMENT_VIEW_DELETE: 22,
    CASH_REGISTER_VIEW_DELETE: 23,
    PAYMENT_EDIT_DATE: 25,
    APPLOG_VIEW: 26,
    FIRM_CASH_REGISTER: 27,
    EXPORT_CASH_VIEW: 32,
    EXPORT_APPOINTMENTS: 33,
    ENTITLEMENT_MANAGER: 34,
    ENTITLEMENT_CONFIRM: 35,
    ENTITLEMENT_UPDATE_APPOINTMENT_RESPONSIBLE_USER: 36,
    ENTITLEMENT_ACCESS_OTHER_RESPONSIBLE_USERS: 37,
    VIEW_PRICE: 38,
    FILTER_LOGS_BY_USER: 39,
    VIEW_ALL_INSTITUTIONS: 40,
    APPOINTMENT_ACCOUNTING: 41,
    EDIT_PAST_APPOINTMENT: 42,
    EDIT_PAST_PAYMENT: 43,
    VIEW_INSTITUTION_NOTE_RESPONSIBLE: 44,
    APPOINTMENT_VIEW_OTHER_USER: 46,
    ENTITLEMENT_RESPONSIBLE_FILTER: 47,
    ENTITLEMENT_EXPORT: 48,
    ENABIZ: 49,
    ENABIZ_OPERATION_TRACKING: 51,
    NOTIFICATION_SETTINGS_MANAGER: 52,
    APPOINTMENT_EDIT_OPERATION: 53,
    DICOM_IMPORT: 54,
    BRANDING_MANAGE: 55,
    CONFIG_MANAGE: 56,

    // Onam formu şablon builder'ı (backend PermissionItems.cs ile birebir)
    FORM_TEMPLATE_MANAGER: 58,

    // Kasa panosu (/app/dashboard sekmesi)
    CASH_DASHBOARD: 59,

    // Randevu Excel'ine telefon sütunu (izin yoksa sütun hiç yazılmaz)
    EXPORT_APPOINTMENTS_PHONE: 60,

    // Randevu türü tanımları (Sistem → Tanımlar → Randevu Türleri)
    APPOINTMENT_TYPE_MANAGER: 61,

    // Süper admin: her izne sahip sayılır (backend PermissionHelper ile aynı kural)
    SUPER_ADMIN: 62,

    // Analiz panoları: her biri /app/dashboard'da bir sekme (backend PermissionItems.cs ile birebir)
    DASHBOARD_PERFORMANCE: 63,
    DASHBOARD_REVENUE: 64,
    DASHBOARD_INSTITUTION: 65,
    DASHBOARD_APPOINTMENT: 66,
    DASHBOARD_OPERATION: 67,
    DASHBOARD_UNIT: 68,
    DASHBOARD_ENTITLEMENT: 69,
    DASHBOARD_CONTROL: 70,
    DASHBOARD_REPORTS: 71,

    // Kapsam: sorumluya bağlı kartlarda herkesin verisi (yoksa yalnız kendi verisi)
    DASHBOARD_ALL_DATA: 72
}

/** Analiz panosu sekmelerini açan izinler (DASHBOARD_ALL_DATA sekme açmaz). */
export const ANALYTICS_BOARD_PERMISSIONS = [
    PERMISSIONS.DASHBOARD_PERFORMANCE,
    PERMISSIONS.DASHBOARD_REVENUE,
    PERMISSIONS.DASHBOARD_INSTITUTION,
    PERMISSIONS.DASHBOARD_APPOINTMENT,
    PERMISSIONS.DASHBOARD_OPERATION,
    PERMISSIONS.DASHBOARD_UNIT,
    PERMISSIONS.DASHBOARD_ENTITLEMENT,
    PERMISSIONS.DASHBOARD_CONTROL,
    PERMISSIONS.DASHBOARD_REPORTS
]

const UserActions = {
    SET_USER: 'SET_USER',
    SET_TOKEN: 'SET_TOKEN',
    SET_COOKIE_SESSION: 'SET_COOKIE_SESSION',
    SET_FIRM_SETTINGS: 'SET_FIRM_SETTINGS',
    SIGN_OUT: 'SIGN_OUT',
    SET_PRICE_LIST: 'SET_PRICE_LIST'
}

const defaultValue = {
    permissionIdList: [],
    role: {
        permissionIdList: [],
    },
    ...LocalStorageService.getUser({
        name: 'test',
        email: 'test'
    }),
    firmSettings: LocalStorageService.getFirmSettings({
        startTime: '08:00',
        endTime: '23:30',
        locale: 'tr-TR'
    }),
    // Oturum artık HttpOnly çerezde; `token` yalnız güncelleme öncesinden kalan (ya da arayüzün
    // API'den farklı origin'de çalıştığı geliştirme kurulumundaki) token'ı tutar.
    token: LocalStorageService.getToken(),
    cookieSession: SessionService.hasCookieSession(),
    priceList: []
}

const UserContext = React.createContext(defaultValue);


function userReducer(state, action) {
    switch (action.type) {
        case UserActions.SET_USER: {
            LocalStorageService.setUser(action.data)

            return {
                ...state,
                ...action.data
            }
        }
        case UserActions.SET_PRICE_LIST: {
            return {
                ...state,
                priceList: action.data.priceList
            }
        }
        case UserActions.SET_FIRM_SETTINGS: {
            LocalStorageService.setFirmSettings(action.data)
            return {
                ...state,
                firmSettings: action.data
            }
        }
        case UserActions.SIGN_OUT: {
            LocalStorageService.setToken()
            LocalStorageService.setUser({})
            LocalStorageService.getFirmSettings({})
            // Çerezleri sunucu siler; yoksa sayfa yenilenince işaret çerezi "hâlâ girişli" derdi.
            ApiService.Auth.Logout()
            // Önbellekteki tanım listeleri kullanıcının firma yetkisine göre süzülü; sonraki girişe taşınmasın.
            clearApiCache()

            return {
                data: {},
                token: null,
                cookieSession: false
            }
        }
        case UserActions.SET_TOKEN: {
            // Giriş yanıtı geldiğinde sunucu çerezi de kurmuş olur. Çerez varsa token'ı SAKLAMAYIZ:
            // localStorage'da durmasının tek sebebi kalırdı ki o da onu XSS'e açık tutmak olurdu.
            //
            // Çerez GÖRÜNMÜYORSA ya arayüz API ile farklı origin'dedir (geliştirme) ya da bu bir
            // personel oturumu değildir (hasta token'ı için sunucu çerez kurmaz) — ikisinde de eski
            // token yolu tek çalışan yol olduğu için korunur.
            const cookieSession = SessionService.hasCookieSession()

            if (cookieSession) {
                // setToken(null) DEĞİL: o çağrı saklı kullanıcıyı da siliyor (çıkış davranışı).
                // Burada sadece token anahtarının gitmesi gerekiyor.
                localStorage.removeItem('token')
            }
            else {
                LocalStorageService.setToken(action.data)
            }

            return {
                ...state,
                token: cookieSession ? null : action.data,
                cookieSession
            }
        }
        case UserActions.SET_COOKIE_SESSION: {
            // Eski token çereze taşındı (Auth/Session); artık localStorage'da tutulmasına gerek yok.
            // setToken(null) kullanıcıyı da temizlediği için burada anahtar doğrudan siliniyor.
            localStorage.removeItem('token')

            return {
                ...state,
                token: null,
                cookieSession: true
            }
        }
        default: {
            return state
        }
    }
}

function UserProvider({ children }) {
    const [user, dispatch] = React.useReducer(userReducer, defaultValue)

    const value = { user, dispatch }

    // Sessiz geçiş: güncellemeden önce girmiş personelin elinde hâlâ geçerli token var, çerezi yok.
    // Token'ı bir kez sunucuya gösterip çereze taşıtıyoruz — kullanıcı hiçbir şey fark etmiyor, kimse
    // yeniden giriş yapmak zorunda kalmıyor. Başarısız olursa token yerinde kalır ve istekler eskisi
    // gibi Authorization header'ı ile gider.
    useEffect(() => {
        if (!user.token || user.cookieSession)
            return

        let cancelled = false;

        (async () => {
            const response = await ApiService.Auth.Session()

            if (!cancelled && response?.success && SessionService.hasCookieSession()) {
                dispatch({ type: UserActions.SET_COOKIE_SESSION })
            }
        })()

        return () => { cancelled = true }
    }, [])

    return (
        <UserContext.Provider value={value}>
            {children}
        </UserContext.Provider>
    )
}

function useUser() {
    const context = React.useContext(UserContext);

    if (!context)
        throw new Error('User Provider Error')

    const { user, dispatch } = context;

    const setToken = (token) => {
        dispatch({
            type: UserActions.SET_TOKEN,
            data: token
        })
    }

    const setUser = (user) => {
        dispatch({
            type: UserActions.SET_USER,
            data: user
        })
    }

    const setPriceList = (priceList) => {
        dispatch({
            type: UserActions.SET_PRICE_LIST,
            data: { priceList }
        })
    }

    const signOut = () => {
        dispatch({
            type: UserActions.SIGN_OUT
        })
    }

    const setFirmSettings = (settings) => {
        dispatch({
            type: UserActions.SET_FIRM_SETTINGS,
            data: settings
        })
    }

    const hasOwnPermission = (permission) => {
        if (!user || !user.role || !user.role.permissionIdList)
            return false;

        return user.role.permissionIdList.includes(permission)
            || (user.permissionIdList ?? []).includes(permission)
    }

    const isSuperAdmin = () => hasOwnPermission(PERMISSIONS.SUPER_ADMIN);

    const hasPermission = (permission) => {
        return isSuperAdmin() || hasOwnPermission(permission);
    }

    const isAuth = () => {
        // Oturum çerezde de olabilir, elde kalan eski token'da da.
        return !!(user && (user.token || user.cookieSession));
    }

    const formatPrice = (price, options = {}) => {
        if (!hasPermission(PERMISSIONS.VIEW_PRICE))
            return "-";

        return Helper.Currency.format(price, options);
    }

    return {
        user,
        priceList: user.priceList || [],
        firmSettings: user ? user.firmSettings : {},
        setToken,
        setUser,
        signOut,
        setFirmSettings,
        hasPermission,
        isSuperAdmin,
        isAuth,
        setPriceList,
        formatPrice
    }
}

export { UserProvider, useUser }

